import {
    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR,
    WIDGET_VERSION,
    WIDGET_VERSION_DATA_ATTR,
} from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsVipTiersSingleProgressWidgetDefinition,
    RewardsVipTiersSingleProgressWidgetV2Definition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import {
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";

import type RewardsVipTiersSingleProgressWidget from "./v1/RewardsVipTiersSingleProgressWidget";
import type RewardsVipTiersSingleProgressWidgetV2 from "./v2/RewardsVipTiersSingleProgressWidgetV2";

class RewardsVipTiersSingleProgressWidgetInjector {
    constructor(private readonly config: RewardsWidgetInjectorConfig) {}

    start(): void {
        this.config.logger.debug(
            "RewardsVipTiersSingleProgressWidgetInjector start",
            {
                placeholderSelector: this.config.placeholderSelector,
            }
        );

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsVipTiersSingleProgressWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            void (async () => {
                const widgetVersion = parseStringDataAttr<WIDGET_VERSION>(
                    placeholder,
                    WIDGET_VERSION_DATA_ATTR
                );

                const isWidgetShadowRootEnabled = parseOptionalBooleanDataAttr(
                    placeholder,
                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                );

                const commonWidgetConfig = {
                    target: placeholder,
                    widgetVersion,
                    isWidgetShadowRootEnabled,
                };

                let widget:
                    | RewardsVipTiersSingleProgressWidget
                    | RewardsVipTiersSingleProgressWidgetV2;
                let widgetName: REWARDS_WIDGET_NAMES;

                if (widgetVersion === WIDGET_VERSION.V2) {
                    const RewardsVipTiersSingleProgressWidgetV2 =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsVipTiersSingleProgressWidgetV2Definition,
                            true
                        );

                    widget = new RewardsVipTiersSingleProgressWidgetV2(
                        commonWidgetConfig,
                        this.config.logger
                    );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_VIP_TIERS_SINGLE_PROGRESS_WIDGET_V2;
                } else {
                    const RewardsVipTiersSingleProgressWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsVipTiersSingleProgressWidgetDefinition,
                            true
                        );

                    widget = new RewardsVipTiersSingleProgressWidget(
                        commonWidgetConfig,
                        this.config.logger
                    );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_VIP_TIERS_SINGLE_PROGRESS_WIDGET;
                }

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsVipTiersSingleProgressWidgetInjector;
